/**
 * Mirror of the API cap (`allocations-repo.ts`). Keeps a product's monthly allocation
 * within the int4 `monthly_cap` column bound so the upsert can't overflow.
 */
export const MAX_MONTHLY_CAP = 1_000_000;

export type RawAllocationRecord = { name: string; units: string };

export type AllocationProduct = { productId: string; productName: string; monthlyCap: number };

export type ImportRow = {
  key: string;
  productId: string | null;
  productName: string;
  ambiguous: boolean;
  units: string;
};

export type RowStatus =
  | { status: "valid"; productId: string; units: number; willOverwrite: boolean }
  | { status: "error"; reason: "unknown_product" | "ambiguous_product" | "invalid_units" };

export type ImportSummary = { identified: number; valid: number; error: number; willOverwrite: number };

const HEADER = ["Products", "Allocated Units"] as const;
const HEADER_FIRST_CELL = new Set(["products", "product", "product name", "product sku", "sku"]);

const normalizeName = (value: string) => value.trim().toLowerCase();
const escapeCell = (value: string) => (/[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

export const buildAllocationsTemplate = (products: { productName: string; monthlyCap: number }[]): string =>
  [HEADER.join(","), ...products.map((p) => [escapeCell(p.productName), String(p.monthlyCap)].join(","))].join("\n");

const parseCsvMatrix = (text: string): string[][] => {
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      record.push(field);
      field = "";
      i += 1;
      continue;
    }
    if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      record.push(field);
      records.push(record);
      record = [];
      field = "";
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  if (field.length > 0 || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  return records;
};

const isHeaderRow = (row: string[]): boolean => HEADER_FIRST_CELL.has(normalizeName(String(row[0] ?? "")));

const recordsFromMatrix = (matrix: string[][]): RawAllocationRecord[] => {
  const rows = matrix.filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""));
  const first = rows[0];
  const start = first && isHeaderRow(first) ? 1 : 0;
  return rows.slice(start).map((row) => ({ name: String(row[0] ?? "").trim(), units: String(row[1] ?? "").trim() }));
};

export const parseAllocationsCsv = (text: string): RawAllocationRecord[] => recordsFromMatrix(parseCsvMatrix(text));

// read-excel-file replaces SheetJS (`xlsx`) here because that package is
// unmaintained on npm and pinned at the version vulnerable to CVE-2023-30533
// (prototype pollution in the parser). The /universal subpath accepts an
// ArrayBuffer directly and works in both the browser bundle and the jsdom
// vitest environment without any platform-specific shimming.
export const parseAllocationsXlsx = async (buffer: ArrayBuffer): Promise<RawAllocationRecord[]> => {
  const { default: readXlsxFile } = await import("read-excel-file/universal");
  const sheets = await readXlsxFile(buffer);
  const firstSheet = sheets[0];
  if (!firstSheet) return [];
  const matrix: string[][] = firstSheet.data.map((row) => row.map((cell) => (cell == null ? "" : String(cell))));
  return recordsFromMatrix(matrix);
};

export const isXlsxFile = (file: File): boolean => /\.xlsx$/i.test(file.name) || file.type.includes("spreadsheetml");

export const readAllocationsFile = async (file: File): Promise<RawAllocationRecord[]> =>
  isXlsxFile(file) ? parseAllocationsXlsx(await file.arrayBuffer()) : parseAllocationsCsv(await file.text());

export const buildImportRows = (records: RawAllocationRecord[], products: AllocationProduct[]): ImportRow[] => {
  const idsByName = new Map<string, string[]>();
  const nameById = new Map<string, string>();
  for (const product of products) {
    nameById.set(product.productId, product.productName);
    const key = normalizeName(product.productName);
    const ids = idsByName.get(key) ?? [];
    ids.push(product.productId);
    idsByName.set(key, ids);
  }
  return records.map((record, index) => {
    const ids = idsByName.get(normalizeName(record.name)) ?? [];
    const productId = ids.length === 1 ? (ids[0] ?? null) : null;
    return {
      key: `row-${index}`,
      productId,
      productName: productId ? (nameById.get(productId) ?? record.name) : record.name,
      ambiguous: ids.length > 1,
      units: record.units,
    };
  });
};

export const classifyImportRow = (row: ImportRow, capByProductId: Map<string, number>): RowStatus => {
  if (row.ambiguous) return { status: "error", reason: "ambiguous_product" };
  if (!row.productId) return { status: "error", reason: "unknown_product" };
  const trimmed = row.units.trim();
  const units = Number(trimmed);
  if (trimmed === "" || !Number.isInteger(units) || units < 0 || units > MAX_MONTHLY_CAP)
    return { status: "error", reason: "invalid_units" };
  const current = capByProductId.get(row.productId) ?? 0;
  return { status: "valid", productId: row.productId, units, willOverwrite: current > 0 && current !== units };
};

export const summarizeImportRows = (rows: ImportRow[], capByProductId: Map<string, number>): ImportSummary => {
  let valid = 0;
  let error = 0;
  let willOverwrite = 0;
  for (const row of rows) {
    const status = classifyImportRow(row, capByProductId);
    if (status.status === "valid") {
      valid += 1;
      if (status.willOverwrite) willOverwrite += 1;
    } else {
      error += 1;
    }
  }
  return { identified: rows.length, valid, error, willOverwrite };
};
